import React, {useEffect, useState} from "react";
import PropTypes from 'prop-types';

import {isMobile} from "../../../components/Util";
import ChooseCameraDialog from "../../../components/mugshot/FaceMeasurer/ChooseCameraDialog";

import {
    addCameraEntryForMediaStream,
    haveEntryForDeviceId,
    getMediaStreamAndDevices
}
    from "../../../components/ChooseCameraUtils";

import {
    SHOW_CAMERA_DENIED_DIALOG_INTERVAL_IN_MILLISECONDS
} from "./MugShotV2Config";

const CAMERA_MEDIA_DEVICE_TYPE = "videoinput";
const VIDEO_WIDTH = 720;
const VIDEO_HEIGHT = 720;

export const canChooseCamera = () =>  {
    const isNotMobile = isMobile() === 'false';
    const canEnumerateCameras = navigator?.mediaDevices?.enumerateDevices !== undefined;
    return isNotMobile && canEnumerateCameras;
}

const CameraChooser = ({ noCameraAccessDialogIsShowing,
                           onUserMediaError,
                           setCameraReady,
                           cameraId,
                           setCameraId,
                           cameraInfoArray,
                           setCameraInfoArray,
}) => {

    // choosingCamera controls whether the camera choice dialog is displayed.  We
    // have a slight delay on display of this dialog to avoid flicker issues.
    const [choosingCamera, setChoosingCamera] = useState(false);

    // choosingCameraInternal is used when testing the state of choosing a camera
    // inside our UseEffect call.
    const [choosingCameraInternal, setChoosingCameraInternal] = useState(false);

    const [enumeratingCameras, setEnumeratingCameras] = useState(false);

    const [cameras, setCameras] = useState([]);

    const chooseCamera = () => {

        // We need to display the camera choice dialog with a delay, otherwise the calls
        // to iterate through the camera properties and tracks cause a flicker on the screen
        // which makes the entire screen repaint in certain browsers.
        setTimeout(setChoosingCamera, 800 , true);
        setChoosingCameraInternal(true);
    };

    const haveWebCamData = () => {
        return takenWebCaptures();
    }

    const takenWebCaptures = () => {
        if ( cameraInfoArray === undefined || cameraInfoArray === null ) {
            return false;
        }

        return cameraInfoArray.length > 0;
    }

    const cameraDeniedOnDesktop = (err) => {

        // Due to the way the React useEffect call works, we will get here multiple times.
        // Simply clear the last timeout and set another so that the final call causes one
        // dialog to appear.
        clearTimeout(onUserMediaErrorInternal);
        setTimeout(onUserMediaErrorInternal, SHOW_CAMERA_DENIED_DIALOG_INTERVAL_IN_MILLISECONDS, err)
    }

    const onDesktopCallback = () => {
        setEnumeratingCameras(false);
    };

    const onUserMediaErrorInternal = (err) => {

        if ( onUserMediaError !== undefined ) {

            onUserMediaError(err,  onDesktopCallback );

            // This will prompt the user again with the modal if they close the modal without
            // enabling camera permissions.
            setCameraReady(false);
        }
    };

    useEffect(() => {

        // We can't have an async useEffect callback function.
        // So instead, use an async IIFE.
        (async function() {

            const canChooseCamera = navigator?.mediaDevices?.enumerateDevices !== undefined;


            if (noCameraAccessDialogIsShowing) {
                return;
            }

            if (haveWebCamData() && choosingCameraInternal) {
                return;
            }

            if (haveWebCamData()) {
                // If we have web camera data, then we already got permission from the camera.  Don't ask again.
                setCameraReady(true);
                return;
            }

            if (canChooseCamera && !enumeratingCameras) {


                if (!cameras || cameras.length < 1) {
                    setEnumeratingCameras(true);

                    try {
                        let {
                            mediaStream, // MediaStream object. https://developer.mozilla.org/en-US/docs/Web/API/MediaStream
                            mediaDevices, // An array of MediaDeviceInfo objects https://developer.mozilla.org/en-US/docs/Web/API/MediaDevices/enumerateDevices
                            cameraAccessFailed
                        } = await getMediaStreamAndDevices(VIDEO_WIDTH,VIDEO_HEIGHT);

                        if (!cameraAccessFailed) {
                            // Request devices a second time. For some unknown reason we don't always get every device on the first try.
                            mediaDevices = await navigator.mediaDevices.enumerateDevices().catch(() => {
                                cameraAccessFailed = true;
                            });
                        }

                        if (cameraAccessFailed) {
                            cameraDeniedOnDesktop("Camera Access failed.");
                            return;
                        }

                        // Asking for permission will have turned on a camera. Turn it off for now.
                        let tracks = mediaStream.getTracks();  // An array of MediaStreamTrack objects

                        let filteredCameras = mediaDevices.filter(({kind}) => kind === CAMERA_MEDIA_DEVICE_TYPE);
                        setCameras(filteredCameras);

                        let hasMultipleCameras = filteredCameras.length > 1;


                        let cameraInfoArr = [];

                        for (const track in tracks) {

                            let mediaStreamTrack = tracks[track]; // https://developer.mozilla.org/en-US/docs/Web/API/MediaStreamTrack

                            if (takenWebCaptures() === false && hasMultipleCameras) {
                                await addCameraEntryForMediaStream(cameraInfoArr, mediaStreamTrack);
                            } else {
                                // A desktop user with one camera.  If we get here, we got permission to use the camera.
                                // Save the camera information to the camera info array so that we avoid unneeded getUserMedia and
                                // and enumerateDevices again which will slow down showing the camera after a retry.
                                let deviceId = mediaStreamTrack.getSettings().deviceId;

                                cameraInfoArr.push({
                                    deviceId: deviceId,
                                    label: mediaStreamTrack.label,
                                    image: "not taken"
                                })

                                setCameraInfoArray(cameraInfoArr);
                            }

                            mediaStreamTrack.stop();
                        }

                        if (takenWebCaptures() === false && hasMultipleCameras) {

                            for (let i = 0; i < filteredCameras.length; i++) {
                                let mediaDevice = filteredCameras[i];

                                let deviceId = mediaDevice.deviceId;

                                if (haveEntryForDeviceId(cameraInfoArr, deviceId)) {
                                    // don't take a screen capture twice.  We already took it above.
                                    continue;
                                }

                                let devicePromise = navigator.mediaDevices.getUserMedia({
                                    video: {
                                        width: VIDEO_WIDTH,
                                        height: VIDEO_HEIGHT,
                                        deviceId: {exact: deviceId}
                                    }
                                });

                                let mediaStreamRet = await Promise.all([devicePromise]);

                                let mediaStreamTrack = mediaStreamRet[0].getTracks()[0];

                                await addCameraEntryForMediaStream(cameraInfoArr, mediaStreamTrack);

                                mediaStreamTrack.stop();
                            }

                            setCameraInfoArray(cameraInfoArr);
                        }


                        let willChooseCamera = false;

                        if (!cameraId && filteredCameras.length > 0) {
                            setCameraId(filteredCameras[0].deviceId);
                            willChooseCamera = true;
                        } else {
                            setCameraReady(true);
                        }

                        if (hasMultipleCameras && !choosingCameraInternal) {
                            // Multiple cameras have been detected so let the patient choose one.
                            if (willChooseCamera) {
                                chooseCamera();
                            }
                        }

                        setEnumeratingCameras(false);

                    } catch (error) {
                        cameraDeniedOnDesktop(error);
                    }

                } else {
                    if ((!choosingCameraInternal) && (!enumeratingCameras)) {
                        setCameraReady(true);
                    }
                }
            } else {
                if (!enumeratingCameras) {
                    setCameraReady(true);
                }
            }
        })()

    }, [setCameras, cameras, setCameraReady, enumeratingCameras,
        setEnumeratingCameras, noCameraAccessDialogIsShowing ]);

    const chooseCameraCallback = (cameraIdChosen) => {
        setChoosingCamera(false);
        setCameraId(cameraIdChosen);
        setCameraReady(true);
    };

    return (
                <ChooseCameraDialog isOpen={choosingCamera && !noCameraAccessDialogIsShowing}
                                    cameraInfoArray={cameraInfoArray}
                                    doneCallback={chooseCameraCallback}
                />

    )
};
CameraChooser.propTypes = {
    noCameraAccessDialogIsShowing: PropTypes.bool.isRequired,
    onUserMediaError: PropTypes.func.isRequired,
    setCameraReady: PropTypes.func.isRequired,
    setCameraId: PropTypes.func.isRequired,
    cameraId: PropTypes.string,
    cameraInfoArray: PropTypes.array.isRequired,
    setCameraInfoArray: PropTypes.func.isRequired

}
export default CameraChooser;
